import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { apiService } from '../../../client/common/apiCallService.js';
import JncMonthPicker from './MonthPicker.jsx';
import CustomSelect from '../common/CustomSelect.jsx';
import { cryptoCurrencyList, fiatCurrencyList } from '../../helpers/constant.js';
import { parseISO, isValid } from 'date-fns';

export default function AddNewStatementModal({ updateStatements, onClose, open }) {
    const clientData = useSelector(state => state.juno.clientData);
    const api_url = useSelector(state => state.config.api_url);
    const {
        balancesCurrencies = [],
        balancesCrypto = [],
        activeCurrencyPage,
    } = useSelector(state => state.juno);

    const [selectedCurrency, setSelectedCurrency] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [reqError, setReqError] = useState('');
    const [data, setData] = useState({
        fromDate: new Date(),
        currency: [],
    });
    const [formattedDate, setFormattedDate] = useState(format(new Date(), 'yyyy-MM'));

    const filteredOptions = useMemo(() => {
        return activeCurrencyPage === 'fiat' ? fiatCurrencyList : cryptoCurrencyList;
    }, [activeCurrencyPage, balancesCurrencies, balancesCrypto]);

    const isFormValid = !!selectedCurrency && !!data.currency;

    const handleCurrencyChange = currency => {
        setSelectedCurrency(currency);
        setData(prev => ({ ...prev, currency: [currency.currencyShortName] }));
        setReqError('');
    };

    const onChangeDate = val => {
        setFormattedDate(format(val, 'yyyy-MM'));
        setData(prev => ({ ...prev, fromDate: val }));
    };

    const resetForm = () => {
        const defaultCurrency = filteredOptions.length > 0 ? filteredOptions[0] : null;
        const resetData = {
            fromDate: new Date(),
            currency: defaultCurrency ? [defaultCurrency.currencyShortName] : [],
        };
        setData(resetData);
        setFormattedDate(format(resetData.fromDate, 'yyyy-MM'));
        setSelectedCurrency(null);
        setReqError('');
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSubmit = async e => {
        e.preventDefault();
        if (!isFormValid) {
            setReqError('* Currency field is required');
            return;
        }
        
        setIsLoading(true);
        apiService(
            `${window.location.hostname === 'localhost' ? api_url : ''}/restapi/generate-client-statement`,
            {
                fromDate: formattedDate,
                currency: data.currency[0],
                currencyType: activeCurrencyPage,
                name: `Statement ${data.currency[0]} ${formattedDate}`,
            },
            () => {
                setIsLoading(false);
                handleClose();
                updateStatements();
            },
            err => {
                setReqError(err?.responseJSON?.err?.message || 'An error occurred');
                setIsLoading(false);
            },
        );
    };
    const createdDate = clientData?.createdAt ? parseISO(clientData.createdAt) : null;
    const minSelectableDate = isValid(createdDate) ? createdDate : undefined;

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
            <div className="relative w-full max-w-md mx-2 rounded-lg shadow-lg bg-modal-bg">
                <div className="flex items-center justify-between px-4 py-3">
                    <h2 className="text-lg font-semibold !text-primary">CREATE NEW STATEMENT</h2>
                    <button onClick={handleClose} className="text-2xl !text-primary">
                        &times;
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="px-4 py-6 space-y-5">
                    <div>
                        <label className="block w-full text-left text-xs font-medium text-balanceCard-manage mb-1">
                            STATEMENT TIME PERIOD
                        </label>
                        <JncMonthPicker
                            selected={data.fromDate}
                            onChangeDate={onChangeDate}
                            formattedDate={formattedDate}
                            minDate={minSelectableDate}
                        />
                    </div>

                    <CustomSelect
                        value={selectedCurrency}
                        onChange={handleCurrencyChange}
                        options={filteredOptions}
                        isError={!!reqError}
                        label="CURRENCY"
                        defaultLabel="SELECT A CURRENCY"
                    />

                    {reqError && <p className="text-sm !text-error">{reqError}</p>}

                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="px-5 py-3 w-full !bg-primary text-[#fff] dark:text-[#18181B] font-bold rounded-lg text-sm disabled:opacity-60">
                            {isLoading ? 'PROCESSING...' : 'GENERATE'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
